import ComicSansMSFont from '@/assets/fonts/ComicSansMS-base64'
import { avifToPngBase64, getImagePath, HOST_URL_IMGS, loadImageBase64 } from './constantes'

export interface PdfData {
  nombreCliente: string
  tipoEvento: string
  catalogoServicios: any[]
  eventDate: string
  guestCount: number
  discountPercent: number
  discountAmount: number
  subtotal: number
  totalWithDiscount: number
  payments: Array<{ amount: number }>
  pendingAmount: number
  observations: string
  selectedItems: any[]
  backgroundRespage: string
  backgroundHeader: string
  signatureImage: string
  mostrarImagenes?: boolean
}

/**
 * Formatea un número como moneda colombiana
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value)
}

/**
 * Formatea un número con separadores de miles
 */
function formatNumber(value: number): string {
  return new Intl.NumberFormat('es-CO').format(value)
}

/**
 * Genera un PDF de cotización con los datos proporcionados
 * @param data - Datos de la cotización
 * @returns Promise<void>
 */
export async function generatePDF(data: PdfData): Promise<void> {
  try {
    const { jsPDF } = (globalThis as any).jspdf
    const doc = new jsPDF()

    doc.addFileToVFS('@/assets/fonts/ComicSansMS.ttf', ComicSansMSFont)
    doc.addFont('@/assets/fonts/ComicSansMS.ttf', 'ComicSansMS', 'normal')

    const pageWidth = doc.internal.pageSize.width
    const pageHeight = doc.internal.pageSize.height
    
    // MÁRGENES AJUSTADOS PARA EVITAR SOBREPOSICIÓN CON EL BANNER
    const topMargin = 35 // Margen superior cuando hay nueva página
    const bottomMargin = 20 // Margen inferior

    // Función helper para agregar el fondo a cualquier página
    const addBackgroundToCurrentPage = (image: string) => {
      doc.addImage(image, 'PNG', 0, 0, pageWidth, pageHeight)
    }

    // ====== PORTADA ======
    addBackgroundToCurrentPage(data.backgroundHeader)

    // ====== PÁGINA DE CONTENIDO ======
    doc.addPage()
    addBackgroundToCurrentPage(data.backgroundRespage)

    // Colores corporativos
    const primaryColor = [223, 165, 163]
    const secondaryColor = [102, 126, 234]
    const darkGray = [51, 51, 51]
    const lightGray = [245, 245, 245]
    const black = [0, 0, 0]
    const white = [255, 255, 255]
    const green = [0, 255, 0]

    let yPos = 25

    // HEADER - Fecha y Tipo de Evento
    doc.setFontSize(10)
    doc.setFont('ComicSansMS', 'normal')
    const fechaTexto = new Date(data.eventDate).toLocaleDateString('es-CO', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
    doc.text(fechaTexto, 20, yPos)
    
    if (data.tipoEvento) {
      doc.setFontSize(9)
      doc.text(`Tipo de Evento: ${data.tipoEvento}`, 20, yPos + 5)
      yPos += 5
    }
    yPos += 15

    // Título principal
    doc.setFontSize(14)
    doc.setTextColor(...black)
    doc.setFont('ComicSansMS', 'normal')
    doc.text(`Queridos clientes, reciban un cordial saludo.`, 20, yPos)
    yPos += 8

    // Subtítulo 1
    doc.setFontSize(12)
    doc.setFont('ComicSansMS', 'normal')
    doc.setTextColor(...black)
    const introText = `A continuación, les presento la propuesta formal de nuestros servicios, donde ofrecemos alternativas cuidadosamente diseñadas para crear el ambiente ideal que deseas proyectar, garantizando una experiencia memorable.`
    const splitIntro = doc.splitTextToSize(introText, 170)
    doc.text(splitIntro, 20, yPos)
    yPos += splitIntro.length * 6 + 5

    // Subtítulo 2
    doc.setFontSize(12)
    doc.setFont('ComicSansMS', 'normal')
    doc.setTextColor(...black)
    const introText2 = `Agradecemos la confianza que depositan en eventos Bibiana Díaz al considerar nuestro acompañamiento para un día tan especial.`
    const splitIntro2 = doc.splitTextToSize(introText2, 170)
    doc.text(splitIntro2, 20, yPos)
    yPos += splitIntro2.length * 6 + 10

    // ====== ORGANIZAR ITEMS POR JERARQUÍA (3 NIVELES) ======
    interface ItemsPorServicio {
      [servicioNombre: string]: {
        secciones: {
          [seccionNombre: string]: any[]
        }
      }
    }

    const itemsPorServicio: ItemsPorServicio = {}

    // Agrupar items por Servicio > Sección
    data.selectedItems.forEach(item => {
      const servicioNombre = item.NombreServicio || 'Sin Servicio'
      const seccionNombre = item.NombreSeccion || 'Sin Sección'

      if (!itemsPorServicio[servicioNombre]) {
        itemsPorServicio[servicioNombre] = { secciones: {} }
      }

      if (!itemsPorServicio[servicioNombre].secciones[seccionNombre]) {
        itemsPorServicio[servicioNombre].secciones[seccionNombre] = []
      }

      itemsPorServicio[servicioNombre].secciones[seccionNombre].push(item)
    })

    // ====== FUNCIÓN AUXILIAR PARA RENDERIZAR IMÁGENES DE UNA SECCIÓN ======
    const renderImagenesSeccion = async (seccion: any[]) => {
      // if (!data.mostrarImagenes) return

      // Recolectar todas las imágenes de los items de esta sección
      const todasLasImagenes: string[] = []
 
      const agrupado = agruparPorSeccion(seccion)
      agrupado.forEach((item: any) => {
        if (item.ImagenesSeccion && item.ImagenesSeccion.length > 0) {
          const imagenes = Array.isArray(item.ImagenesSeccion) ? item.ImagenesSeccion : JSON.parse(item.ImagenesSeccion || '[]')
          todasLasImagenes.push(...imagenes)
        }
      })

      // Si no hay imágenes, salir
      if (todasLasImagenes.length === 0) return

      yPos += 5 // Espacio antes de las imágenes

      // Configuración de imágenes
      const tamanoImagen = 30
      const imgWidth = 35 + (tamanoImagen)
      const imgHeight = 25 + (tamanoImagen - 10)

      const imgSpacing = 5
      const imgsPerRow = 2
      const leftMargin = 35
      
      let imgCount = 0
      let xOffset = leftMargin

      for (const imagenUrl of todasLasImagenes) {
        // Nueva fila si es necesario
        if (imgCount > 0 && imgCount % imgsPerRow === 0) {
          yPos += imgHeight + imgSpacing
          xOffset = leftMargin
          
          // Verificar si necesitamos nueva página
          if (yPos + imgHeight > pageHeight - bottomMargin) {
            doc.addPage()
            addBackgroundToCurrentPage(data.backgroundRespage)
            yPos = topMargin
            xOffset = leftMargin
          }
        }
        
 
        try {
          // Cargar y agregar la imagen
          // si la url es base 64 no usar getImagePath
          let url = imagenUrl
          let base64 = ''
          if (!imagenUrl.startsWith('data:image')) {
            url = getImagePath(imagenUrl)
            base64 = await loadImageBase64(url)
          } else {
            if (imagenUrl.includes('avif')) {
              base64 = await avifToPngBase64(imagenUrl)
            } else {
              base64 = imagenUrl
            }
          }
          
          // Agregar la imagen al PDF
          // Cuando usas base64, el formato se detecta automáticamente del string base64
          doc.addImage(base64, xOffset, yPos, imgWidth, imgHeight)

          // Borde
          doc.setDrawColor(200, 200, 200)
          doc.setLineWidth(0.2)
          doc.rect(xOffset, yPos, imgWidth, imgHeight)

          xOffset += imgWidth + imgSpacing
          imgCount++
        } catch (error) {
          console.warn('Error al agregar imagen:', imagenUrl, error)
        }
      }

      // Ajustar yPos después de las imágenes
      if (imgCount > 0) {
        const rowsUsed = Math.ceil(imgCount / imgsPerRow)
        yPos += imgHeight + (rowsUsed > 1 ? imgSpacing : 0) + 5
      }
    }

    // ====== RENDERIZAR SERVICIOS CON JERARQUÍA E IMÁGENES POR SECCIÓN ======
    // IMPORTANTE: Necesitamos usar un bucle for...of con await en lugar de forEach
    for (const servicioNombre of Object.keys(itemsPorServicio)) {
      // Verificar si necesitamos nueva página
      if (yPos > 245) {
        doc.addPage()
        addBackgroundToCurrentPage(data.backgroundRespage)
        yPos = topMargin // Usar margen superior ajustado
      }

      // Título del SERVICIO (Nivel 1)
      doc.setFillColor(...primaryColor)
      doc.rect(15, yPos - 5, 180, 8, 'F')
      doc.setFontSize(12)
      doc.setTextColor(255, 255, 255)
      doc.setFont('ComicSansMS', 'normal')
      doc.text(servicioNombre.toUpperCase(), 20, yPos)
      yPos += 10

      // Renderizar SECCIONES (Nivel 2)
      const secciones = itemsPorServicio[servicioNombre].secciones
      
      for (const seccionNombre of Object.keys(secciones)) {
        // Verificar espacio para la sección
        if (yPos > 265) {
          doc.addPage()
          addBackgroundToCurrentPage(data.backgroundRespage)
          yPos = topMargin
        }

        // Título de la SECCIÓN
        doc.setFontSize(10)
        doc.setTextColor(...black)
        doc.setFont('ComicSansMS', 'normal')
        doc.text(seccionNombre, 25, yPos)
        yPos += 8

        // Items de la sección (Nivel 3) - SIN IMÁGENES
        doc.setFontSize(9)
        doc.setTextColor(...darkGray)
        doc.setFont('ComicSansMS', 'normal')
        
        secciones[seccionNombre].forEach(item => {
          // Verificar espacio antes de agregar item
          if (yPos > 270) {
            doc.addPage()
            addBackgroundToCurrentPage(data.backgroundRespage)
            yPos = topMargin
          }
          
          // Renderizar solo el nombre del item (sin imágenes)
          const cantItem = item.Cantidad && item.Cantidad > 1 ? `${item.Cantidad}x ` : ''
          doc.text(`• ${cantItem}${item.NombreItem}`, 30, yPos)
          yPos += 5
        })
        
        // ====== MOSTRAR TODAS LAS IMÁGENES DE LA SECCIÓN AL FINAL ======
        // USAR AWAIT para esperar a que las imágenes se carguen
        await renderImagenesSeccion(secciones[seccionNombre])
        
        yPos += 3 // Espacio entre secciones
      }
      
      yPos += 5 // Espacio entre servicios
    }

    // ====== CALCULAR ESPACIO NECESARIO PARA LA TABLA ======
    // Preparar datos de la tabla primero
    const tableData: any[] = []

    Object.keys(itemsPorServicio).forEach((servicioNombre) => {
      let totalServicio = 0
      const secciones = itemsPorServicio[servicioNombre].secciones

      Object.keys(secciones).forEach((seccionNombre) => {
        secciones[seccionNombre].forEach(item => {
          totalServicio += parseFloat(item.PrecioUnitario || 0) * (item.Cantidad || 1)
        })
      })

      tableData.push([servicioNombre, formatCurrency(totalServicio)])
    })

    tableData.push(['', ''])
    tableData.push(['Subtotal', formatCurrency(data.subtotal)])
    
    if (data.discountPercent > 0) {
      tableData.push([`Descuento (${data.discountPercent}%)`, `-${formatCurrency(data.discountAmount)}`])
    }

    tableData.push(['Total con Descuento', formatCurrency(data.totalWithDiscount)])
     let totalAbonos = 0
    data.payments.forEach((payment: any, idx: number) => {
      const monto = payment.Monto ? Number(payment.Monto) : payment.amount
      if (monto > 0) {
        totalAbonos += monto
        tableData.push([`Abono ${idx + 1}`, `$ ${formatNumber(monto)}`])
      }
    })
    
    tableData.push(['TOTAL ABONADO', `$ ${formatNumber(totalAbonos)}`])

    if (totalAbonos > 0) {
      tableData.push(['', ''])
    }

    tableData.push(['SALDO PENDIENTE', formatCurrency(data.pendingAmount)])

    // Estimar altura de la tabla (aproximadamente 8mm por fila + 15mm para header y título)
    const estimatedTableHeight = (tableData.length * 8) + 25
    
    // Si no hay suficiente espacio, crear nueva página
    if (yPos + estimatedTableHeight > pageHeight - bottomMargin) {
      doc.addPage()
      addBackgroundToCurrentPage(data.backgroundRespage)
      yPos = topMargin
    } else {
      yPos += 10 // Espacio antes del título
    }

    // ====== TABLA DE RESUMEN ======
    doc.setFontSize(14)
    doc.setFillColor(...primaryColor)
    doc.rect(15, yPos - 5, 180, 8, 'F')
    doc.setTextColor(...white)
    doc.setFont('ComicSansMS', 'normal')
    doc.text('RESUMEN DE SERVICIOS', 20, yPos)
    yPos += 10

    // Índices para estilos
    const subtotalIndex = tableData.findIndex(row => row[0] === 'Subtotal')
    const descuentoIndex = tableData.findIndex(row => row[0].includes('Descuento'))
    const totalIndex = tableData.findIndex(row => row[0] === 'Total con Descuento')
    const saldoIndex = tableData.findIndex(row => row[0] === 'SALDO PENDIENTE')
    const abonoIndex = tableData.findIndex(row => row[0] === 'Abono')
    const totalAbonadoIndex = tableData.findIndex(row => row[0] === 'TOTAL ABONADO')

    doc.autoTable({
      startY: yPos,
      head: [['CONCEPTO', 'VALOR']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: black,
        fontSize: 11,
        fontStyle: 'bold',
        halign: 'center',
        font: 'ComicSansMS'
      },
      bodyStyles: {
        fontSize: 10,
        textColor: black,
        font: 'ComicSansMS'
      },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 60, halign: 'right', fontStyle: 'bold' }
      },
      alternateRowStyles: {
        fillColor: lightGray
      },
      didParseCell: function(cellData: any) {
        const rowIndex = cellData.row.index

        if (cellData.cell.raw === '') {
          cellData.cell.styles.fillColor = [255, 255, 255]
          cellData.cell.styles.minCellHeight = 3
        }

        if (rowIndex < subtotalIndex && cellData.cell.raw !== '') {
          cellData.cell.styles.fillColor = [250, 250, 250]
          cellData.cell.styles.fontStyle = 'bold'
        }

        if (rowIndex === subtotalIndex) {
          cellData.cell.styles.fillColor = [240, 240, 240]
          cellData.cell.styles.fontStyle = 'bold'
          cellData.cell.styles.fontSize = 11
        }

        if (rowIndex === descuentoIndex && data.discountPercent > 0) {
          cellData.cell.styles.fillColor = [255, 240, 240]
          cellData.cell.styles.textColor = [200, 0, 0]
          cellData.cell.styles.fontStyle = 'bold'
        }

        if (rowIndex === totalIndex) {
          cellData.cell.styles.fillColor = primaryColor
          cellData.cell.styles.textColor = [255, 255, 255]
          cellData.cell.styles.fontSize = 12
          cellData.cell.styles.fontStyle = 'bold'
        }

        if (cellData.cell.raw && cellData.cell.raw.toString().includes('Abono')) {
          cellData.cell.styles.fillColor = [240, 255, 240]
          cellData.cell.styles.textColor = [0, 150, 0]
        }

        if (rowIndex === totalAbonadoIndex) {
          cellData.cell.styles.fillColor = [240, 255, 240]
          cellData.cell.styles.textColor = [0, 150, 0]  
          cellData.cell.styles.fontSize = 12 
          cellData.cell.styles.valign = 'middle'
          cellData.cell.styles.minCellHeight = 8 
          cellData.cell.styles.fontStyle = 'bold'
        }

        if (rowIndex === saldoIndex) {
          cellData.cell.styles.fillColor = [255, 243, 205]
          cellData.cell.styles.textColor = [255, 152, 0]
          cellData.cell.styles.fontSize = 12
          cellData.cell.styles.fontStyle = 'bold'
        }
      },
      didDrawPage: function(pageData: any) {
        if (pageData.pageNumber > 2) {
          addBackgroundToCurrentPage(data.backgroundRespage)
        }
      }
    })

    // ====== PÁGINA DE TÉRMINOS Y CONDICIONES ======
    doc.addPage()
    addBackgroundToCurrentPage(data.backgroundRespage)

    doc.setFontSize(19)
    doc.setTextColor(60, 60, 60)
    doc.setFont('ComicSansMS', 'normal')
    doc.text('FORMA DE PAGO', pageWidth / 2, 40, { align: 'center' })

    doc.setFontSize(18)
    doc.setFont('ComicSansMS', 'normal')

    const leftMargin = 10
    const rightMargin = 10
    const textWidth = pageWidth - leftMargin - rightMargin

    const text1 = 'Realizar un primer abono para reservar la fecha y cerrar contrato, los posteriores pagos se realizarán según acuerdo de pago.'
    const splitText1 = doc.splitTextToSize(text1, textWidth)
    doc.text(splitText1, leftMargin, 58, { align: 'justify', maxWidth: textWidth })

    const text2a = 'La boda debe estar 100% cancelada un mes antes del gran día.'
    const splitText2a = doc.splitTextToSize(text2a, textWidth)
    doc.text(splitText2a, leftMargin, 85, { align: 'justify', maxWidth: textWidth })

    const text2b = 'Otros eventos deben estar pagados en su totalidad 1 semana antes del evento.'
    const splitText2b = doc.splitTextToSize(text2b, textWidth)
    doc.text(splitText2b, leftMargin, 100, { align: 'justify', maxWidth: textWidth })

    const text3 = 'Consignar a la cuenta de ahorros Bancolombia: #17596443559 A nombre de Bibiana Diaz Arteaga (C.C 1047411141) Una vez realizada la transacción envíenos el comprobante para enviarle el recibido.'
    const splitText3 = doc.splitTextToSize(text3, textWidth)
    doc.text(splitText3, leftMargin, 130, { align: 'justify', maxWidth: textWidth })

    const text4 = 'Me encuentro a su disposición para atender hasta el más mínimo detalle, y brindarle toda la asesoría necesaria para alcanzar el éxito que usted desea.'
    const splitText4 = doc.splitTextToSize(text4, textWidth)
    doc.text(splitText4, leftMargin, 170, { align: 'justify', maxWidth: textWidth })

    const text5 = 'De la manera más atenta y presta a resolver cualquier inquietud o sugerencia que tengan.'
    const splitText5 = doc.splitTextToSize(text5, textWidth)
    doc.text(splitText5, leftMargin, 200, { align: 'justify', maxWidth: textWidth })

    // Firma
    if (data.signatureImage) {
      doc.addImage(data.signatureImage, 'PNG', 15, pageHeight - 55, 60, 20)
    }

    // Guardar PDF
    const fileName = `Cotizacion_${data.nombreCliente.replace(/\s+/g, '_')}_${data.tipoEvento ? data.tipoEvento.replace(/\s+/g, '_') + '_' : ''}${new Date().toISOString().split('T')[0]}.pdf`
    doc.save(fileName)

    return Promise.resolve()
  } catch (error) {
    console.error('Error generando PDF:', error)
    return Promise.reject(error)
  }
}
 
function agruparPorSeccion(data: any[]) {
  return Object.values(
    data.reduce((acc, item) => {
      const key = item.NombreSeccion

      if (!acc[key]) {
        acc[key] = {
          NombreSeccion: key,
          SeccionID: item.SeccionID,
          ServicioID: item.ServicioID,
          NombreServicio: item.NombreServicio,
          ImagenesSeccion: JSON.parse(item.ImagenesSeccion),
          items: []
        }
      }

      acc[key].items.push({
        ItemID: item.ItemID,
        NombreItem: item.NombreItem,
        Cantidad: item.Cantidad || 1,
        PrecioUnitario: item.PrecioUnitario,
        Subtotal: item.Subtotal,
        ObservacionesDetalle: item.ObservacionesDetalle || '',
        Imagenes: item.Imagenes
      })

      return acc
    }, {})
  )
}